import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import {
  REQUEST_LIST_API,
  REQUEST_LIST_COUNT_API,
} from '../../../constants/api'
import { EMPTY_OBJECT } from '../../../constants/common'
import { REQUEST_ENTITY } from '../../../constants/entity'
import { ScreenContainerRef } from '../../../context'
import {
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import { isSubArchive } from '../../../utils/route'
import { PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { RequestTableLayout, useRequestTablePage } from '../../Request'
import { TablePagination } from '../../TablePagination'
import { getEntityTabFilterStateApi } from '../EntityTab.api'
import {
  EntityTabSubMenuSubTabs,
  useEntityTabSubMenuSubTab,
} from '../EntityTabSubMenuSubTabs'

import {
  ENTITY_TAB_REQUESTS_DEFAULT_QA_NAME,
  ENTITY_TAB_REQUESTS_SUB_TAB_QA_NAME,
} from './EntityTabRequests.constants'
import { useEntityTabRequestsCreation } from './EntityTabRequests.hooks'
import {
  getEntityTabRequestsScopeFilter,
  getEntityTabRequestsSubTabCount,
  hasEntityTabRequestsItems,
  hasEntityTabRequestsVisibleFilters,
  isEntityTabRequestsToolbarCollapsed,
} from './EntityTabRequests.utils'
import { EntityTabRequestsToolbar } from './EntityTabRequestsToolbar'

import * as styles from './EntityTabRequests.module.css'

import ServiceRequestIcon from '../../../icons/service-request.svg'

function EntityTabRequests({
  countApi = REQUEST_LIST_COUNT_API,
  counters = EMPTY_OBJECT,
  creationBuildingApiParams,
  creationInitialBuilding,
  creationInitialFlat,
  creationInitialUser,
  creationInitialValues,
  emptyStateTextKey = 'Request.NoDataKey',
  entity = REQUEST_ENTITY,
  listApi = REQUEST_LIST_API,
  qaName = ENTITY_TAB_REQUESTS_DEFAULT_QA_NAME,
  scopeFilterKey,
  scopeId,
  shouldShowCreateAction = true,
  onApplyTableConfig,
  onRegisterReload,
  onReloadCounters,
  onReloadOwnCounters,
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  function handleCloseModal() {
    setModal(null)
  }

  const { activeSubTab, setActiveSubTab, setSubMenu, subMenu } =
    useEntityTabSubMenuSubTab()

  const isArchiveSubTab = isSubArchive(activeSubTab)

  const asyncTableOptions = {
    countApi,
    extraFilters: getEntityTabRequestsScopeFilter({ scopeFilterKey, scopeId }),
    filterStateApi: getEntityTabFilterStateApi({
      entityPlural: entity.plural,
      objectId: scopeId,
      sub: activeSubTab,
    }),
    handleSetSubMenu: setSubMenu,
    listApi,
    shouldCheckSub: true,
    withoutSubMenu: true,
  }

  const tablePage = useRequestTablePage({
    asyncTableOptions,
    entity,
    extraSelectionResetDeps: [activeSubTab, scopeId],
    setModal,
    sub: activeSubTab,
    withoutBrowserTabTitle: true,
    onApplyTableConfig,
    onCloseModal: handleCloseModal,
  })

  const {
    availableFilters,
    filterValues,
    handleChangeShouldReloadFilters,
    handleLoadInit,
    handleReload,
    handleSetFilterValues,
    handleSetPagination,
    hasFilterChanged,
    isInitialLoadSettled,
    isLoading,
    isPerformingMassAction,
    isRefetching,
    items: requests,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    shouldReloadFilters,
  } = tablePage

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReload,
    onReloadCounters,
    onReloadOwnCounters,
  })

  // Expose the table reload to the profile so out-of-tab openers (e.g. the
  // actions widget "Add new service request") can refresh a visible list.
  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [activeSubTab, scopeId])

  const { handleAddAction, isWizardEnabled, requestCreationModal } =
    useEntityTabRequestsCreation({
      creationBuildingApiParams,
      creationInitialBuilding,
      creationInitialFlat,
      creationInitialUser,
      creationInitialValues,
      onClose: handleCloseModal,
      onReload: handleReload,
    })

  const hasRequests = hasEntityTabRequestsItems(requests)

  const showEmptyState = !hasRequests && !isLoading

  const shouldShowCreate = shouldShowCreateAction && permissions?.can_create

  const shouldShowCreateButton = shouldShowCreate && !isArchiveSubTab

  const shouldShowEmptyStateActions =
    shouldShowCreateButton && !hasFilterChanged

  const emptyStateActions = getEmptyStateActions({
    actionName: qaName.addNew,
    isLoading,
    qaName: qaName.addNew,
    shouldShowActions: shouldShowEmptyStateActions,
    text: t('RequestTable.AddNewBtnKey'),
    onClick: handleAddAction,
  })

  const hasVisibleFilters = hasEntityTabRequestsVisibleFilters(availableFilters)

  const shouldCollapseToolbar = isEntityTabRequestsToolbarCollapsed({
    hasFilterChanged,
    hasRequests,
    hasVisibleFilters,
    isLoading,
    shouldShowCreateButton,
  })

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div className={styles.EntityTabRequests} ref={pageScrollWrapperRef}>
        {isPerformingMassAction || isRefetching ? <PageLoader /> : null}
        <EntityTabSubMenuSubTabs
          activeSubTab={activeSubTab}
          counters={counters}
          getCount={getEntityTabRequestsSubTabCount}
          qaName={ENTITY_TAB_REQUESTS_SUB_TAB_QA_NAME}
          subMenu={subMenu}
          onChange={setActiveSubTab}
        />
        {shouldCollapseToolbar ? null : (
          <EntityTabRequestsToolbar
            activeSubTab={activeSubTab}
            availableFilters={availableFilters}
            entity={entity}
            filterValues={filterValues}
            hasVisibleFilters={hasVisibleFilters}
            isLoading={isLoading}
            isWizardEnabled={isWizardEnabled}
            ordering={ordering}
            pagination={pagination}
            qaName={qaName}
            scopeFilterKey={scopeFilterKey}
            scopeId={scopeId}
            shouldReloadFilters={shouldReloadFilters}
            shouldShowCreateButton={shouldShowCreateButton}
            onAddAction={handleAddAction}
            onChangeShouldReloadFilters={handleChangeShouldReloadFilters}
            onReload={handleReload}
            onSetFilterValues={handleSetFilterValues}
          />
        )}
        <div
          className={classNames(styles.content, {
            [styles.contentEmpty]: showEmptyState,
          })}
        >
          <RequestTableLayout
            emptyState={
              <PageEmptyState
                EntityIcon={ServiceRequestIcon}
                actions={emptyStateActions}
                text={t(emptyStateTextKey)}
              />
            }
            entity={entity}
            extraModals={requestCreationModal}
            hasTable={!showEmptyState}
            modal={modal}
            tablePage={tablePage}
          />
        </div>
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      </div>
    </ScreenContainerRef>
  )
}

export default EntityTabRequests
